import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  CircularProgress,
  Icon,
  Switch,
  FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Formik, FormikProps, Form } from 'formik';
import * as Yup from 'yup';
import theme from '../../../../../assets/theme';
import FaiBox from "../../../../../MuiComponents/FaiBox";
import FaiTypography from "../../../../../MuiComponents/FaiTypography";
import FaiButton from '../../../../../MuiComponents/FaiButton';
import FormField from '../../../../../components/FormField';
import FaiSelect from '../../../../../MuiComponents/FaiSelect';
import FaiInput from '../../../../../MuiComponents/FaiInput';
import SalesController from '../../../../../controllers/saleController';
import { Sale } from '../../../../../types/orderTypes';
import { AxiosResponse } from 'axios';

interface EditSaleModalProps {
  open: boolean;
  onClose: () => void;
  saleId: string | null;
  onSaleUpdated: () => void;
}

interface SelectOption {
  value: string;
  label: string;
}

// Define a more specific API response type that might contain nested data
interface ApiResponse<T> {
  status: number;
  message: string | null;
  errors: any | null;
  data: T;
  timeStamp: number;
}

// Define form values interface
interface SaleFormValues {
  brand: string;
  products: string[];
  discount: string;
  discountType: 'PERCENTAGE' | 'FLAT';
  startDate: string;
  endDate: string;
  status: string;
  description: string;
}

// Backend Utils.parseLocalDate expects dd-MM-yyyy; <input type="date"> emits yyyy-MM-dd.
const toBackendDate = (iso: string): string => {
  if (!iso) return iso;
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};

// Sales are scoped by brand + category, not individual products, so there's no
// products field to require here.
const validationSchema = Yup.object().shape({
  brand: Yup.string().required('Brand is required'),
  discount: Yup.number().required('Discount is required').positive('Discount must be positive'),
  discountType: Yup.string().required('Discount type is required'),
  startDate: Yup.string().required('Start date is required'),
  endDate: Yup.string().required('End date is required'),
  description: Yup.string().required('Description is required')
});

function EditSaleModal({ open, onClose, saleId, onSaleUpdated }: EditSaleModalProps) {
  const { typography, palette } = theme;
  const [loading, setLoading] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [productOptions, setProductOptions] = useState<SelectOption[]>([]);

  const [initialValues, setInitialValues] = useState<SaleFormValues>({
    brand: '',
    products: [],
    discount: '',
    discountType: 'PERCENTAGE',
    startDate: '',
    endDate: '',
    status: 'ACTIVE',
    description: ''
  });

  useEffect(() => {
    const fetchSaleData = async () => {
      if (!saleId || !open) return;
      
      setLoading(true);
      try {
        // Get the sale data
        const response: AxiosResponse = await SalesController.getSale(saleId);
        console.log('Sale data:', response.data);
        
        // Extract the sale data from the response
        let saleData: Sale;
        
        // Check if response.data has a nested 'data' property
        if (response.data && typeof response.data === 'object' && 'data' in response.data) {
          // It's the nested API response format
          saleData = (response.data as ApiResponse<Sale>).data;
        } else {
          // It's the direct format
          saleData = response.data as Sale;
        }
        
        // Format dates to YYYY-MM-DD for the date inputs
        const formatDate = (dateString: string) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        // Set the initial values for the form
        setInitialValues({
          brand: saleData.brand || '',
          products: Array.isArray(saleData.products) ? saleData.products : [],
          discount: saleData.percentageFlat ? saleData.percentageFlat.toString() : '',
          discountType: saleData.discountType || 'PERCENTAGE',
          startDate: formatDate(saleData.validFromDateTime),
          endDate: formatDate(saleData.validTillDateTime),
          status: saleData.status || 'ACTIVE',
          description: saleData.description || ''
        });
        
        // Create mock product options based on the data
        // In a real app, you might want to fetch the actual product list
        if (Array.isArray(saleData.products)) {
          const productOpts = saleData.products.map((product: string) => ({
            value: product,
            label: product
          }));
          setProductOptions(productOpts);
        }
      } catch (error) {
        console.error('Error fetching sale:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSaleData();
  }, [saleId, open]);

  const handleSubmit = async (values: SaleFormValues, { setSubmitting }: any) => {
    if (!saleId) return;
    
    setSubmitLoading(true);
    try {
      // Map to the backend UpdateSaleRequestDTO field names (the old payload used
      // discount/discountType/startDate which the backend ignored, so edits
      // silently didn't apply). Status is managed from the list toggle, not here.
      const payload: any = {
        brand: values.brand,
        saleType: values.discountType,
        percentageFlat: parseFloat(values.discount),
        validFromDate: toBackendDate(values.startDate),
        validTillDate: toBackendDate(values.endDate),
        validFromTime: '00:00',
        validTillTime: '23:59',
        description: values.description,
      };

      await SalesController.updateSale(saleId, payload);
      onSaleUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating sale:', error);
    } finally {
      setSubmitLoading(false);
      setSubmitting(false);
    }
  };

  const discountTypeOptions: SelectOption[] = [
    { value: 'PERCENTAGE', label: 'Percentage (%)' },
    { value: 'FLAT', label: 'Flat Amount' }
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <FaiBox display="flex" justifyContent="space-between" alignItems="center">
          <FaiTypography variant="h5" fontWeight={typography.fontWeightMedium}>
            Edit Sale
          </FaiTypography>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </FaiBox>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <FaiBox display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress />
          </FaiBox>
        ) : (
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {(props: FormikProps<SaleFormValues>) => {
              const { 
                values, 
                touched, 
                errors, 
                handleChange, 
                handleBlur, 
                setFieldValue
              } = props;
              
              return (
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        label="Brand"
                        name="brand"
                        placeholder="Enter Brand Name"
                        value={values.brand}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.brand && Boolean(errors.brand)}
                        helperText={touched.brand && errors.brand}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FaiBox mb={3}>
                        <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <FaiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Discount Type
                          </FaiTypography>
                        </FaiBox>
                        <FaiSelect
                          placeholder="Select Discount Type"
                          name="discountType"
                          value={values.discountType}
                          options={discountTypeOptions}
                          onChange={(selectedOption: SelectOption) => {
                            setFieldValue("discountType", selectedOption.value);
                          }}
                        />
                      </FaiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        label="Discount"
                        name="discount"
                        placeholder={`Enter discount ${values.discountType === 'PERCENTAGE' ? 'percentage' : 'amount'}`}
                        value={values.discount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.discount && Boolean(errors.discount)}
                        helperText={touched.discount && errors.discount}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        type="date"
                        label="Start Date"
                        name="startDate"
                        value={values.startDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.startDate && Boolean(errors.startDate)}
                        helperText={touched.startDate && errors.startDate}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        type="date"
                        label="End Date"
                        name="endDate"
                        value={values.endDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.endDate && Boolean(errors.endDate)}
                        helperText={touched.endDate && errors.endDate}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormField
                        multiline
                        rows={4}
                        label="Description"
                        name="description"
                        placeholder="Enter Sale Description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.description && Boolean(errors.description)}
                        helperText={touched.description && errors.description}
                      />
                    </Grid>
                  </Grid>
                  
                  <FaiBox mt={3} display="flex" justifyContent="flex-end">
                    <FaiButton 
                      variant="outlined"
                      color="secondary"
                      onClick={onClose}
                      sx={{ marginRight: 2 }}
                    >
                      Cancel
                    </FaiButton>
                    <FaiButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitLoading}
                    >
                      {submitLoading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
                    </FaiButton>
                  </FaiBox>
                </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default EditSaleModal;
